import { formatBytes } from '../ui/bytes'
import { relativeAge } from '../ui/relativeTime'
import type { BackupJob } from '../transport/api'

/**
 * WHAT THE PERSON MUST BE TOLD — owner, 2026-09-29, in these words' order:
 *
 *   我們要給使用者有充分的訊息當使用者要 backup 的時候, 我們要讓他知道他的 password
 *   我們是不保存的, 丟失了就沒有辦法 recover, 他如果重新設置一個 password, 他之前 backup
 *   的 agent 就會全部的流失, 而且在設置新的 password 的時候, 需要將他之前所有的 backup
 *   的東西都清除.
 *
 * ONE COPY. The vault setup, every backup, and the reset all say these, and three
 * hand-typed versions would drift into three different promises.
 *
 * "A new password" here means a NEW VAULT. Changing the password with the recovery code
 * keeps the backups (it re-wraps the same key) — the one door that does not lose them,
 * and the reason the recovery code exists; the words keep the two apart.
 */
export const VAULT_PROMISES = {
  notStored: 'We never store your vault password. We can’t show it to you or reset it for you.',
  noRecovery:
    'If you lose both your password and your recovery code, your backups can’t be opened — not by you, not by us.',
  newVaultDeletes:
    'Setting up a new vault permanently deletes every backup made with the old one.',
} as const

/** What a phase means to the person, with the numbers that tell moving from stuck. */
export function jobLine(job: BackupJob, machine: string): string {
  switch (job.phase) {
    case 'queued':
      return `Waiting for another backup on ${machine} to finish…`
    case 'packing': {
      const pct = job.total > 0 ? Math.min(99, Math.floor((job.done / job.total) * 100)) : 0
      return `Packing on ${machine}… ${pct}%`
    }
    case 'uploading':
      return `Encrypting and uploading… ${job.done} of ${job.parts} part${job.parts === 1 ? '' : 's'}`
    case 'committing':
      return 'Finishing…'
    case 'done':
      return `${formatBytes(job.packed_size)} · encrypted on ${machine} to your vault`
    case 'failed':
      return 'The backup did not finish.'
  }
}

/** 0..1 across the whole job, for the bar: packing is the first fifth, uploading the rest. */
export function jobFraction(job: BackupJob): number {
  if (job.phase === 'done') return 1
  if (job.phase === 'packing') return job.total > 0 ? 0.2 * Math.min(1, job.done / job.total) : 0
  if (job.phase === 'uploading') return 0.2 + (job.parts > 0 ? 0.78 * (job.done / job.parts) : 0)
  if (job.phase === 'committing') return 0.98
  return 0
}

/**
 * Why a backup did not happen, for the person. The machine's and the cloud's own
 * sentence where theirs is the useful one; ours where a code needs an action they
 * would not guess.
 */
export function whyBackupFailed(code: string | null | undefined, message: string | null | undefined, ctx: { machine: string; accountBytes?: number | undefined }): string {
  switch (code) {
    case 'quota_exceeded':
      return `This backup would go past your account’s backup space${
        ctx.accountBytes ? ` (${formatBytes(ctx.accountBytes)})` : ''
      }. Delete older backups under More → Backups, then try again.`
    case 'no_vault':
      return 'Set up your backup vault first.'
    case 'backup_unconfigured':
      return 'Backups are not available on this server yet.'
    case 'cloud_too_old':
      return 'Your Avengents cloud does not support backups yet.'
    case 'not_found':
    case 'unknown_route':
      return `${ctx.machine} needs an Avengents update before it can back up agents.`
    case 'cloud_unreachable':
      return `${ctx.machine} could not reach the Avengents cloud. Check its connection and try again.`
    case 'upload_failed':
      return `${message ?? 'An upload failed.'} Check ${ctx.machine}’s connection and try again.`
    default:
      return message || code || 'The backup did not finish.'
  }
}

/** Seconds-since as the Backups list says it: "just now", "5m ago", "2d ago". */
export function backupAge(ts: number, nowS: number = Date.now() / 1000): string {
  const a = relativeAge(nowS - ts)
  return a === 'now' ? 'just now' : a
}
